import React, { useState } from "react";
import { IconMeta } from "./FooterContacts";

export function LinearHeroMockup() {
  const [activeTab, setActiveTab] = useState<"swarms" | "pipeline" | "evals">("swarms");

  return (
    <div className="linear-mockup-wrapper">
      <div className="linear-mockup-window">
        {/* Top Window Bar */}
        <div className="mockup-header">
          <div className="mockup-traffic-lights">
            <span className="dot dot-red" />
            <span className="dot dot-yellow" />
            <span className="dot dot-green" />
          </div>

          <div className="mockup-breadcrumbs">
            <span className="mockup-crumb-org">automatrix</span>
            <span className="mockup-crumb-sep">/</span>
            <span className="mockup-crumb-proj">mission-control</span>
            <span className="mockup-crumb-sep">/</span>
            <span className="mockup-crumb-status">● live_swarm</span>
          </div>

          <div className="mockup-status-meta">
            <div className="meta-badge-indicator">
              <IconMeta size={12} />
              <span>Meta Cloud API: Connected</span>
            </div>
            <span className="latency-indicator">24ms · GMT-3</span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mockup-tabs">
          <button
            className={`mockup-tab ${activeTab === "swarms" ? "active" : ""}`}
            onClick={() => setActiveTab("swarms")}
          >
            <span className="tab-glyph">⚡</span>
            <span>Active Swarms (3)</span>
          </button>
          <button
            className={`mockup-tab ${activeTab === "pipeline" ? "active" : ""}`}
            onClick={() => setActiveTab("pipeline")}
          >
            <span className="tab-glyph">⇄</span>
            <span>Meta CRM Gateway</span>
          </button>
          <button
            className={`mockup-tab ${activeTab === "evals" ? "active" : ""}`}
            onClick={() => setActiveTab("evals")}
          >
            <span className="tab-glyph">✓</span>
            <span>Deterministic Evals</span>
          </button>
        </div>

        {/* Dynamic Interactive Body */}
        <div className="mockup-body">
          {activeTab === "swarms" && (
            <div className="mockup-content-grid">
              <div className="agent-card">
                <div className="agent-card-header">
                  <div className="agent-avatar sdr">SDR</div>
                  <div className="agent-info">
                    <strong>Autonomous Lead Triaging</strong>
                    <span>WhatsApp Cloud API · GoHighLevel</span>
                  </div>
                  <span className="agent-badge live">Executing</span>
                </div>
                <div className="agent-activity-line">
                  <span className="terminal-prompt">$</span>
                  <code>qualify_inbound(contact="+1415***", intent="enterprise_arch")</code>
                </div>
                <div className="agent-telemetry">
                  <span>Confidence: 99.2%</span>
                  <span>Human Gate: Passed</span>
                  <span>Tokens: 412</span>
                </div>
              </div>

              <div className="agent-card">
                <div className="agent-card-header">
                  <div className="agent-avatar intake">INT</div>
                  <div className="agent-info">
                    <strong>Candidate Voice Assessment</strong>
                    <span>Sub-second Latency · AssemblyAI</span>
                  </div>
                  <span className="agent-badge idle">Standby</span>
                </div>
                <div className="agent-activity-line">
                  <span className="terminal-prompt">$</span>
                  <code>await_audio_stream(target="RecrutaCIS")</code>
                </div>
                <div className="agent-telemetry">
                  <span>DISC Profiler: Ready</span>
                  <span>Audio Latency: 480ms</span>
                </div>
              </div>

              <div className="agent-card">
                <div className="agent-card-header">
                  <div className="agent-avatar audit">OPS</div>
                  <div className="agent-info">
                    <strong>Cloudflare Edge Sync</strong>
                    <span>D1 SQLite · Vectorize · KV</span>
                  </div>
                  <span className="agent-badge live">Syncing</span>
                </div>
                <div className="agent-activity-line">
                  <span className="terminal-prompt">$</span>
                  <code>replicate_state(region="gmt-3", edge="iad1")</code>
                </div>
                <div className="agent-telemetry">
                  <span>Replication: 0 lag</span>
                  <span>SSL: Cloudflare One</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "pipeline" && (
            <div className="mockup-pipeline-view">
              <div className="pipeline-node-row">
                <span className="pipeline-step-badge">STEP 01</span>
                <span className="pipeline-desc">Meta Webhook Ingestion &amp; Signature Validation (SHA-256)</span>
                <span className="pipeline-status">VERIFIED (100%)</span>
              </div>
              <div className="pipeline-node-row">
                <span className="pipeline-step-badge">STEP 02</span>
                <span className="pipeline-desc">GoHighLevel Contact Mutex &amp; Deduplication Lock</span>
                <span className="pipeline-status">0 CONFLICTS</span>
              </div>
              <div className="pipeline-node-row">
                <span className="pipeline-step-badge">STEP 03</span>
                <span className="pipeline-desc">LLM Structured Tool Calling with Zod Schema Guard</span>
                <span className="pipeline-status">ZERO HALLUCINATIONS</span>
              </div>
              <div className="pipeline-node-row">
                <span className="pipeline-step-badge">STEP 04</span>
                <span className="pipeline-desc">WhatsApp Interactive Button Message Dispatch</span>
                <span className="pipeline-status">DELIVERED &lt; 200ms</span>
              </div>
            </div>
          )}

          {activeTab === "evals" && (
            <div className="mockup-evals-view">
              <div className="eval-metric-grid">
                <div className="eval-card">
                  <span className="eval-card-num">99.8%</span>
                  <span className="eval-card-lbl">Deterministic Output Accuracy</span>
                </div>
                <div className="eval-card">
                  <span className="eval-card-num">&lt; 500ms</span>
                  <span className="eval-card-lbl">Voice Latency (AssemblyAI + Cartesia)</span>
                </div>
                <div className="eval-card">
                  <span className="eval-card-num">0.00%</span>
                  <span className="eval-card-lbl">Hallucination Rate on Production Prompts</span>
                </div>
                <div className="eval-card">
                  <span className="eval-card-num">168</span>
                  <span className="eval-card-lbl">Audited Code Repositories</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
